import React, { useState, useEffect } from 'react';
import { WaterCraft } from './AddWaterCraftModal';

interface EditableWaterCraftCardProps {
  waterCraft: WaterCraft;
  index: number;
  onSave: (index: number, updatedData: WaterCraft) => void;
  onCancel: (index: number) => void;
  onEdit: (index: number) => void;
  cardNumber: number;
}

const EditableWaterCraftCard: React.FC<EditableWaterCraftCardProps> = ({
  waterCraft,
  index,
  onSave,
  onCancel,
  onEdit,
  cardNumber
}) => {
  const [isEditing, setIsEditing] = useState(waterCraft.isEditing || false);
  const [editedData, setEditedData] = useState<WaterCraft>({ ...waterCraft });

  // Sync with parent component's editing state
  useEffect(() => {
    setIsEditing(waterCraft.isEditing || false);
  }, [waterCraft.isEditing]);

  // Reset edited data when waterCraft prop changes
  useEffect(() => {
    setEditedData({ ...waterCraft });
  }, [waterCraft]);

  const handleSave = () => {
    console.log('💾 Saving water craft edits:', editedData);
    onSave(index, { ...editedData, isEditing: false });
    setIsEditing(false);
  };

  const handleCancel = () => {
    console.log('❌ Canceling water craft edits');
    onCancel(index);
    setIsEditing(false);
    setEditedData({ ...waterCraft }); // Reset to original data
  };

  const handleEdit = () => {
    console.log('✏️ Editing water craft');
    onEdit(index);
    setIsEditing(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    let newValue: any = value;
    
    if (type === 'number') {
      newValue = value === '' ? undefined : parseFloat(value);
    } else if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    }
    
    console.log(`🔄 Changing ${name} from ${editedData[name as keyof WaterCraft]} to ${newValue}`);
    
    setEditedData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const formatCurrency = (value: number | undefined) => {
    if (!value && value !== 0) return 'R 0.00';
    return `R ${value.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (isEditing) {
    return (
      <div className="bg-white border border-blue-300 rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-semibold text-blue-600">Water Craft #{cardNumber} (Editing)</h4>
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
        
        <div className="space-y-3 text-sm">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-600 text-xs mb-1">Year *</label>
              <input
                type="number"
                name="year"
                value={editedData.year || ''}
                onChange={handleInputChange}
                className="w-full p-2 border rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-xs mb-1">Craft Type *</label>
              <select
                name="craftType"
                value={editedData.craftType || ''}
                onChange={handleInputChange}
                className="w-full p-2 border rounded text-sm"
              >
                <option value="">Select type</option>
                <option value="sail">Sail</option>
                <option value="ski">Ski</option>
                <option value="motor">Motor</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-gray-600 text-xs mb-1">Vessel Name *</label>
            <input
              type="text"
              name="vesselName"
              value={editedData.vesselName || ''}
              onChange={handleInputChange}
              className="w-full p-2 border rounded text-sm"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-600 text-xs mb-1">Hull Make *</label>
              <input
                type="text"
                name="hullMake"
                value={editedData.hullMake || ''}
                onChange={handleInputChange}
                className="w-full p-2 border rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-xs mb-1">Hull Length (meters) *</label>
              <input
                type="number"
                step="0.1"
                name="hullLength"
                value={editedData.hullLength || ''}
                onChange={handleInputChange}
                className="w-full p-2 border rounded text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-600 text-xs mb-1">Total Sum Insured *</label>
            <input
              type="number"
              name="totalSumInsured"
              value={editedData.totalSumInsured || ''}
              onChange={handleInputChange}
              className="w-full p-2 border rounded text-sm"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h4 className="font-semibold text-blue-600">Water Craft #{cardNumber}</h4>
          <div className="flex space-x-2">
            <button
              onClick={handleEdit}
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
            >
              Edit
            </button>
          </div>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Year:</span>
              <span className="font-medium">{waterCraft.year}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Type:</span>
              <span className="font-medium">{waterCraft.craftType}</span>
            </div>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Vessel Name:</span>
            <span className="font-medium">{waterCraft.vesselName}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Hull Make:</span>
            <span className="font-medium">{waterCraft.hullMake}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Hull Length:</span>
            <span className="font-medium">{waterCraft.hullLength} meters</span>
          </div>
          
          {waterCraft.totalSumInsured && (
            <div className="flex justify-between">
              <span className="text-gray-600">Total Sum Insured:</span>
              <span className="font-medium text-green-600">
                {formatCurrency(waterCraft.totalSumInsured)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditableWaterCraftCard;